import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../panel/api";
import { EASE } from "@/components/Motion";
import { inputCls } from "@/panel/components";

export default function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.new_password.length < 8) return setError("Password must be at least 8 characters.");
    if (pw.new_password !== pw.confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, new_password: pw.new_password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4 py-16">
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[480px] w-[480px] rounded-full bg-gold/[0.07] blur-[140px]" />
      <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative w-full max-w-md"
        data-testid="reset-card"
      >
        <Link to="/panel/login" data-testid="reset-back-link"
          className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-neutral-500 transition-colors hover:text-goldlight">
          <ArrowLeft size={14} /> BACK TO LOGIN
        </Link>

        <div className="rounded-3xl border border-gold/15 bg-panel/80 p-8 backdrop-blur-xl sm:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/40 bg-gold/10 text-gold">
              <KeyRound size={18} />
            </span>
            <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">PASSWORD RESET</div>
          </div>

          {done ? (
            <div className="mt-8 text-center" data-testid="reset-success">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
              <h1 className="mt-5 font-serif text-3xl italic text-neutral-50">All set.</h1>
              <p className="mt-2 text-sm text-neutral-400">Your password has been updated.</p>
              <Link to="/panel/login" data-testid="reset-login-link"
                className="mt-6 inline-block rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-8 py-3.5 text-sm font-bold text-black">
                LOG IN
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mt-8 font-serif text-3xl italic text-neutral-50" data-testid="reset-heading">
                New key, same empire.
              </h1>
              {!token && (
                <p className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-300" data-testid="reset-no-token">
                  This link is missing its token — request a fresh reset from the login page.
                </p>
              )}
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                <input type="password" required placeholder="New password (min. 8 characters)"
                  value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                  className={inputCls} data-testid="reset-password-input" />
                <input type="password" required placeholder="Confirm new password"
                  value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                  className={inputCls} data-testid="reset-confirm-input" />
                {error && (
                  <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-300"
                    data-testid="reset-error">
                    {error}
                  </motion.p>
                )}
                <button type="submit" disabled={busy || !token} data-testid="reset-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)] disabled:opacity-50">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  SET NEW PASSWORD
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
